// MedicalProfilePanel — foundation for blueprint modules #2 (Medical
// Profile), #13 (Medication Monitor), #14 (Doctor Intelligence), and
// #15 (AI Client Memory).
//
// One panel per client, three collapsible sections:
//   Care Team       — doctors, hospitals, pharmacies, network prefs
//   Health Detail   — conditions, prescriptions, upcoming procedures
//   Personal Fabric — family notes, interests (surfaced pre-call by
//                     the AI Executive Assistant in blueprint #20)
//
// Data model: client_medical_profile (see migration 136). RLS-scoped
// to owner_id. Writes are debounced-on-blur (no ceremony — the agent
// types, hits tab, it saves). Uses supabase client directly so it
// tolerates a missing table (soft-degrades to read-only "coming soon"
// UI before the migration runs).

import React from 'react';
import { supabase } from '../lib/supabase';

const emptyProfile = () => ({
  preferred_language: '',
  preferred_contact_method: '',
  best_time_to_call: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  primary_doctor: '',
  primary_doctor_phone: '',
  specialists: [],
  preferred_hospital: '',
  urgent_care_pref: '',
  dental_provider: '',
  vision_provider: '',
  mental_health_provider: '',
  network_preferences: '',
  medical_conditions: [],
  upcoming_surgeries: [],
  preferred_pharmacy: '',
  preferred_pharmacy_phone: '',
  current_prescriptions: [],
  personal_notes: '',
  family_context: [],
  interests: [],
});

export default function MedicalProfilePanel({ clientId, ownerId, showToast = () => {} }) {
  const [profile, setProfile]   = React.useState(emptyProfile());
  const [loading, setLoading]   = React.useState(true);
  const [saving,  setSaving]    = React.useState(false);
  const [missing, setMissing]   = React.useState(false); // true when the table isn't there yet
  const [dirty,   setDirty]     = React.useState(false);

  React.useEffect(() => {
    if (!clientId || !ownerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('client_medical_profile')
          .select('*')
          .eq('owner_id', ownerId)
          .eq('client_id', clientId)
          .maybeSingle();
        if (cancelled) return;
        if (error && /relation .* does not exist/i.test(error.message || '')) {
          setMissing(true);
        } else if (data) {
          setProfile({ ...emptyProfile(), ...data });
        }
      } catch (err) {
        // Table missing or RLS blocked — degrade to placeholder.
        if (/does not exist/i.test(err?.message || '')) setMissing(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const save = React.useCallback(async () => {
    if (missing || !clientId || !ownerId || !dirty) return;
    setSaving(true);
    try {
      const row = { ...profile, owner_id: ownerId, client_id: clientId };
      // Drop server-managed columns so upsert doesn't fight the trigger.
      delete row.id; delete row.updated_at; delete row.updated_by;
      const { error } = await supabase
        .from('client_medical_profile')
        .upsert(row, { onConflict: 'owner_id,client_id' });
      if (error) throw error;
      setDirty(false);
    } catch (err) {
      showToast(`Couldn't save medical profile: ${err.message || err}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [profile, clientId, ownerId, dirty, missing, showToast]);

  const patch = (key, val) => { setProfile(p => ({ ...p, [key]: val })); setDirty(true); };

  if (loading) {
    return <div className="text-xs text-slate-500 italic px-1 py-2">Loading medical profile…</div>;
  }

  if (missing) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2.5 text-xs text-amber-900">
        <strong>Medical profile not yet migrated.</strong> Run
        {' '}<code className="font-mono">supabase_migrations/136_client_medical_profile.sql</code>{' '}
        to enable this panel. Blueprint modules 2, 13, 14, 15 depend on it.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Save indicator — quiet chip, doesn't demand a click */}
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          Medical profile
        </div>
        <div className="text-[10.5px] text-slate-500">
          {saving ? 'Saving…' : dirty ? (
            <button onClick={save} className="text-emerald-700 font-semibold hover:underline">
              Save changes
            </button>
          ) : 'Saved'}
        </div>
      </div>

      {/* ─ Section 1 · Care Team (blueprint #14 Doctor Intelligence) ─ */}
      <Section title="Care team" hint="Doctors, hospitals, pharmacies — the AI remembers these on renewals.">
        <Grid>
          <Field label="Primary doctor" value={profile.primary_doctor} onChange={v => patch('primary_doctor', v)} onBlur={save}/>
          <Field label="Doctor phone"   value={profile.primary_doctor_phone} onChange={v => patch('primary_doctor_phone', v)} onBlur={save}/>
          <Field label="Preferred hospital" value={profile.preferred_hospital} onChange={v => patch('preferred_hospital', v)} onBlur={save}/>
          <Field label="Urgent care" value={profile.urgent_care_pref} onChange={v => patch('urgent_care_pref', v)} onBlur={save}/>
          <Field label="Dental provider" value={profile.dental_provider} onChange={v => patch('dental_provider', v)} onBlur={save}/>
          <Field label="Vision provider" value={profile.vision_provider} onChange={v => patch('vision_provider', v)} onBlur={save}/>
          <Field label="Mental health provider" value={profile.mental_health_provider} onChange={v => patch('mental_health_provider', v)} onBlur={save}/>
          <Field label="Network preferences" hint="e.g. HMO OK · needs Dr. Smith in-network" value={profile.network_preferences} onChange={v => patch('network_preferences', v)} onBlur={save}/>
        </Grid>
        <ArrayField
          label="Specialists"
          items={profile.specialists || []}
          template={{ name: '', type: '', phone: '' }}
          columns={['name', 'type', 'phone']}
          onChange={arr => { patch('specialists', arr); }}
          onBlur={save}
        />
        <Grid>
          <Field label="Preferred pharmacy" value={profile.preferred_pharmacy} onChange={v => patch('preferred_pharmacy', v)} onBlur={save}/>
          <Field label="Pharmacy phone" value={profile.preferred_pharmacy_phone} onChange={v => patch('preferred_pharmacy_phone', v)} onBlur={save}/>
        </Grid>
      </Section>

      {/* ─ Section 2 · Health Detail (blueprint #2 + #13) ─ */}
      <Section title="Health detail" hint="Conditions and prescriptions the AI cross-checks against carrier formularies on renewals.">
        <ArrayField
          label="Current prescriptions"
          items={profile.current_prescriptions || []}
          template={{ name: '', dosage: '', generic_ok: false }}
          columns={['name', 'dosage', 'generic_ok']}
          onChange={arr => patch('current_prescriptions', arr)}
          onBlur={save}
        />
        <ArrayField
          label="Medical conditions"
          items={profile.medical_conditions || []}
          template={{ condition: '', note: '' }}
          columns={['condition', 'note']}
          onChange={arr => patch('medical_conditions', arr)}
          onBlur={save}
        />
        <ArrayField
          label="Upcoming surgeries / procedures"
          items={profile.upcoming_surgeries || []}
          template={{ date: '', procedure: '' }}
          columns={['date', 'procedure']}
          onChange={arr => patch('upcoming_surgeries', arr)}
          onBlur={save}
        />
      </Section>

      {/* ─ Section 3 · Personal fabric (blueprint #15 AI Client Memory) ─ */}
      <Section title="Personal fabric" hint="The AI surfaces these before every call so the conversation feels personal — not transactional.">
        <textarea
          value={profile.personal_notes || ''}
          onChange={e => patch('personal_notes', e.target.value)}
          onBlur={save}
          rows={4}
          placeholder="Life stuff worth remembering — grandkids' names, hobbies, upcoming travel, favorite football team, retirement plans…"
          className="w-full text-[12.5px] border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-500 focus:outline-none"
        />
        <ArrayField
          label="Family context"
          items={profile.family_context || []}
          template={{ relation: '', name: '', note: '' }}
          columns={['relation', 'name', 'note']}
          onChange={arr => patch('family_context', arr)}
          onBlur={save}
        />
        <Grid>
          <Field label="Preferred language" value={profile.preferred_language} onChange={v => patch('preferred_language', v)} onBlur={save}/>
          <Field label="Best time to call" value={profile.best_time_to_call} onChange={v => patch('best_time_to_call', v)} onBlur={save}/>
          <Field label="Preferred contact method" hint="call · text · email" value={profile.preferred_contact_method} onChange={v => patch('preferred_contact_method', v)} onBlur={save}/>
          <Field label="Emergency contact" value={profile.emergency_contact_name} onChange={v => patch('emergency_contact_name', v)} onBlur={save}/>
          <Field label="Emergency phone" value={profile.emergency_contact_phone} onChange={v => patch('emergency_contact_phone', v)} onBlur={save}/>
        </Grid>
      </Section>
    </div>
  );
}

function Section({ title, hint, children }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 text-left"
      >
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{title}</div>
          {hint && <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
        <span className="text-slate-400 text-sm">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="p-3 space-y-3">{children}</div>}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">{children}</div>;
}

function Field({ label, value, onChange, onBlur, hint }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-600">
        {label}
        {hint && <span className="ml-1 text-slate-400 font-normal normal-case tracking-normal">({hint})</span>}
      </div>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full mt-0.5 text-[12.5px] border border-slate-300 rounded-md px-2.5 py-1.5 focus:border-emerald-500 focus:outline-none"
      />
    </label>
  );
}

// Simple array-of-objects editor. Each row shows a compact input per
// column. Add appends an empty; remove drops the row. All writes go
// through onChange with the whole array so the parent stays authoritative.
function ArrayField({ label, items, template, columns, onChange, onBlur }) {
  const add = () => onChange([...(items || []), { ...template }]);
  const remove = (i) => onChange((items || []).filter((_, idx) => idx !== i));
  const setAt = (i, key, val) => {
    const next = [...(items || [])];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-600">{label}</div>
        <button
          type="button"
          onClick={add}
          className="text-[10.5px] font-semibold text-emerald-700 hover:text-emerald-900"
        >+ Add row</button>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-[11px] text-slate-400 italic px-1">None yet.</div>
      ) : (
        <div className="space-y-1">
          {(items || []).map((row, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              {columns.map(col => {
                const isBool = typeof template[col] === 'boolean';
                return isBool ? (
                  <label key={col} className="flex items-center gap-1 text-[11px] text-slate-600 px-1">
                    <input
                      type="checkbox"
                      checked={!!row[col]}
                      onChange={e => setAt(i, col, e.target.checked)}
                      onBlur={onBlur}
                    />
                    {col.replace(/_/g, ' ')}
                  </label>
                ) : (
                  <input
                    key={col}
                    type="text"
                    placeholder={col}
                    value={row[col] || ''}
                    onChange={e => setAt(i, col, e.target.value)}
                    onBlur={onBlur}
                    className="flex-1 text-[12px] border border-slate-300 rounded-md px-2 py-1 focus:border-emerald-500 focus:outline-none"
                  />
                );
              })}
              <button
                type="button"
                onClick={() => { remove(i); onBlur?.(); }}
                className="text-slate-400 hover:text-rose-600 text-[13px] px-1"
                title="Remove row"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
